import React from 'react'
import { Box, Typography } from '@mui/material'
import { INTERAX_COLORS, INTERAX_TYPOGRAPHY } from '../../../constants/colors'
import { QuoteVisualization } from './visualizations'
import { useTranslation } from '@/shared/hooks/useIntractionTranslate'

export interface TraitPill {
  /**
   * Metric/trait name
   */
  name: string
  /**
   * Performance level
   */
  level: 'excellent' | 'good' | 'needsWork'
  /**
   * Level label to display
   */
  levelLabel: string
}

export interface QuoteVisualizationData {
  /**
   * Quote text
   */
  text: string
  /**
   * Speaker name
   */
  speaker: string
}

export interface IndividualProfileSectionProps {
  /**
   * Profile name (gradient title)
   */
  profileName: string
  /**
   * Description text
   */
  description: string
  /**
   * Personality trait pills (shown if no visualization)
   */
  traits: TraitPill[]
  /**
   * Optional quote visualization (takes priority over pills)
   */
  visualization?: QuoteVisualizationData
  /**
   * Optional header title
   */
  headerTitle?: string
  /**
   * Optional header icon
   */
  headerIcon?: React.ReactNode
}

/**
 * Gets gradient color for pill indicator based on level
 */
const getPillGradient = (level: 'excellent' | 'good' | 'needsWork'): string => {
  switch (level) {
    case 'excellent':
      return `linear-gradient(360deg, ${INTERAX_COLORS.green} 0%, ${INTERAX_COLORS.teamHealthGreenDark} 23.08%, ${INTERAX_COLORS.white} 100%)`
    case 'good':
      return `linear-gradient(360deg, #FFD196 0%, ${INTERAX_COLORS.sun} 23.08%, ${INTERAX_COLORS.white} 100%)`
    case 'needsWork':
      return `linear-gradient(360deg, ${INTERAX_COLORS.redSoft} 0%, ${INTERAX_COLORS.redStrong} 23.08%, ${INTERAX_COLORS.white} 100%)`
  }
}

/**
 * Gets text color for pill level
 */
const getLevelColor = (level: 'excellent' | 'good' | 'needsWork'): string => {
  switch (level) {
    case 'excellent':
      return INTERAX_COLORS.green
    case 'good':
      return INTERAX_COLORS.sun
    case 'needsWork':
      return INTERAX_COLORS.redStrong
  }
}

/**
 * Gets glow color for pill indicator
 */
const getGlowColor = (level: 'excellent' | 'good' | 'needsWork'): string => {
  switch (level) {
    case 'excellent':
      return `0px 0px 10px rgba(0, 0, 0, 0.5), 0px 0px 2px ${INTERAX_COLORS.teamHealthGreenDark}`
    case 'good':
      return `0px 0px 10px rgba(0, 0, 0, 0.5), 0px 0px 20px ${INTERAX_COLORS.sun40}`
    case 'needsWork':
      return '0px 0px 10px rgba(0, 0, 0, 0.5), 0px 0px 20px rgba(213, 82, 82, 0.4)'
  }
}

/**
 * IndividualProfileSection - Displays individual profile with traits or quote
 *
 * Features:
 * - Responsive two-column layout (row on desktop, column on mobile)
 * - Left: Profile name and description
 * - Right: Quote visualization OR Personality trait pills with level indicators
 * - Conditional rendering: visualization takes priority over pills
 * - Variant-aware background colors for individual styling
 */
export const IndividualProfileSection: React.FC<IndividualProfileSectionProps> = ({
  profileName,
  description,
  traits,
  visualization,
  headerTitle,
  headerIcon
}) => {
  const { t } = useTranslation()

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: '24px',
        width: '100%'
      }}
    >
      <Box
        sx={{
          flex: 1,
          boxSizing: 'border-box',
          display: 'flex',
          flexDirection: 'column',
          padding: '32px',
          gap: '16px',
          background: INTERAX_COLORS.heritageBlue40,
          border: '1px solid rgba(255, 255, 255, 0.08)',
          boxShadow: '0px 4px 16px rgba(0, 0, 0, 0.2), inset 0px 1px 0px 1px rgba(255, 255, 255, 0.1)',
          backdropFilter: 'blur(6px)',
          borderRadius: '16px'
        }}
      >
        {/* Header Section */}
        {(headerTitle || headerIcon) && (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              gap: '16px',
              padding: '0px'
            }}
          >
            {/* Icon */}
            {headerIcon && (
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'row',
                  justifyContent: 'center',
                  alignItems: 'center',
                  padding: '0px',
                  width: '48px',
                  height: '48px',
                  background: INTERAX_COLORS.sun20,
                  borderRadius: '10px',
                  flex: 'none'
                }}
              >
                {headerIcon}
              </Box>
            )}

            {/* Title */}
            {headerTitle && (
              <Typography
                sx={{
                  ...INTERAX_TYPOGRAPHY.headline3,
                  color: INTERAX_COLORS.white
                }}
              >
                {headerTitle}
              </Typography>
            )}
          </Box>
        )}

        {/* Profile Content */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'column', lg: 'row' },
            gap: '24px',
            width: '100%'
          }}
        >
          {/* Part 1: Profile Name & Description */}
          <Box
            sx={{
              flex: 1,
              boxSizing: 'border-box',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              padding: '32px',
              gap: '16px',
              background: 'linear-gradient(144.63deg, rgba(255, 255, 255, 0.08) 0%, rgba(255, 255, 255, 0.02) 100%)',
              border: '1px solid rgba(255, 255, 255, 0.08)',
              boxShadow: '0px 4px 16px rgba(0, 0, 0, 0.2), inset 0px 1px 0px 1px rgba(255, 255, 255, 0.1)',
              backdropFilter: 'blur(6px)',
              borderRadius: '16px'
            }}
          >
            {/* Gradient Profile Name */}
            <Typography
              sx={{
                fontFamily: 'Lexend Deca',
                fontWeight: 700,
                fontSize: '36px',
                lineHeight: '44px',
                background: 'linear-gradient(90deg, #FFD196 0%, #FFAB40 50%, #FFFFFF 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
                textAlign: 'center',
                filter: 'drop-shadow(0px 0px 20px rgba(255, 171, 64, 0.4))'
              }}
            >
              {profileName}
            </Typography>

            {/* Description */}
            <Typography
              sx={{
                ...INTERAX_TYPOGRAPHY.body2,
                color: INTERAX_COLORS.thunderGrey,
                textAlign: 'center',
                maxWidth: '600px'
              }}
            >
              {description}
            </Typography>
          </Box>

          {/* Part 2: Quote Visualization OR Personality Traits Pills */}
          <Box
            sx={{
              flex: 1,
              boxSizing: 'border-box',
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'center',
              alignItems: 'center',
              padding: '24px',
              gap: '10px',
              background: 'linear-gradient(144.63deg, rgba(255, 255, 255, 0.08) 0%, rgba(255, 255, 255, 0.02) 100%)',
              border: '1px solid rgba(255, 255, 255, 0.08)',
              boxShadow: '0px 4px 16px rgba(0, 0, 0, 0.2), inset 0px 1px 0px 1px rgba(255, 255, 255, 0.1)',
              backdropFilter: 'blur(6px)',
              borderRadius: '16px'
            }}
          >
            {visualization ? (
              // Show Quote Visualization if available
              <QuoteVisualization text={visualization.text} speaker={visualization.speaker} variant="individual" />
            ) : (
              // Show Personality Traits Pills if no visualization
              <Box
                sx={{
                  boxSizing: 'border-box',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  padding: '24px',
                  gap: '24px',
                  isolation: 'isolate',
                  position: 'relative',
                  flex: 1,
                  background: INTERAX_COLORS.heritageBlue40,
                  backgroundBlendMode: 'soft-light, normal',
                  border: `1px solid ${INTERAX_COLORS.white04}`,
                  boxShadow: 'inset 0px 1px 0px rgba(255, 255, 255, 0.08)',
                  borderRadius: '10px',
                  overflow: 'hidden'
                }}
              >
                {/* Orange glow ellipse at bottom */}
                <Box
                  sx={{
                    position: 'absolute',
                    width: '244px',
                    height: '177px',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    bottom: '-100px',
                    background: INTERAX_COLORS.sun,
                    filter: 'blur(75px)',
                    zIndex: 2
                  }}
                />

                {/* Title */}
                <Typography
                  sx={{
                    ...INTERAX_TYPOGRAPHY.headline5,
                    color: INTERAX_COLORS.white,
                    alignSelf: 'stretch',
                    zIndex: 3
                  }}
                >
                  {t('gamer.feedback.profile.personalityTraitsTitle', 'Personality traits')}
                </Typography>

                {/* Description */}
                <Typography
                  sx={{
                    ...INTERAX_TYPOGRAPHY.body2,
                    color: INTERAX_COLORS.thunderGrey,
                    alignSelf: 'stretch',
                    zIndex: 3
                  }}
                >
                  {t(
                    'gamer.feedback.profile.individualTraitsDescription',
                    'An overview of your strengths during this gaming session.'
                  )}
                </Typography>

                {/* Pills Container */}
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'flex-start',
                    padding: '0px',
                    gap: '16px',
                    flexWrap: 'wrap',
                    alignSelf: 'stretch',
                    zIndex: 4
                  }}
                >
                  {traits.map((trait, index) => (
                    <Box
                      key={index}
                      sx={{
                        display: 'flex',
                        flexDirection: 'row',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        padding: '12px 12px 12px 18px',
                        gap: '16px',
                        height: '56px',
                        background: INTERAX_COLORS.offBlack80,
                        boxShadow: `inset 2px 2px 5px ${INTERAX_COLORS.white25}`,
                        borderRadius: '40px',
                        flex: 1,
                        minWidth: '200px'
                      }}
                    >
                      {/* Left: Trait name and level */}
                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'flex-start',
                          padding: '0px',
                          flex: 1
                        }}
                      >
                        <Typography
                          sx={{
                            ...INTERAX_TYPOGRAPHY.body4,
                            color: INTERAX_COLORS.offWhiteText
                          }}
                        >
                          {trait.name}
                        </Typography>
                        <Typography
                          sx={{
                            ...INTERAX_TYPOGRAPHY.body4,
                            color: getLevelColor(trait.level)
                          }}
                        >
                          {trait.levelLabel}
                        </Typography>
                      </Box>

                      {/* Right: Glowing circle indicator */}
                      <Box
                        sx={{
                          position: 'relative',
                          width: '32px',
                          height: '32px',
                          flexShrink: 0
                        }}
                      >
                        {/* Outer circle (shadow) */}
                        <Box
                          sx={{
                            position: 'absolute',
                            width: '32px',
                            height: '32px',
                            background: 'rgba(33, 38, 41, 0.4)',
                            boxShadow: 'inset 2px 2px 3px rgba(0, 0, 0, 0.5)',
                            borderRadius: '50%'
                          }}
                        />
                        {/* Inner gradient circle */}
                        <Box
                          sx={{
                            position: 'absolute',
                            left: '4px',
                            top: '4px',
                            width: '24px',
                            height: '24px',
                            background: getPillGradient(trait.level),
                            border: `1px solid ${INTERAX_COLORS.white25}`,
                            boxShadow: getGlowColor(trait.level),
                            borderRadius: '50%'
                          }}
                        />
                      </Box>
                    </Box>
                  ))}
                </Box>
              </Box>
            )}
          </Box>
        </Box>
      </Box>
    </Box>
  )
}

export default IndividualProfileSection
